/* eslint-disable jsx-a11y/anchor-is-valid */
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { H1, H2 } from 'components/01-atoms/Heading';
import { useAnalyticsContext } from 'components/04-templates/AnalyticsProvider';
import { Lesson, TableOfContentsSectionData } from 'types/CourseTypes';
// TODO: USE BIT DEV
import { routes } from 'lib/routes';
import { COURSE_INTRODUCTION } from 'lib/constants';
import { useUserType } from 'components/04-templates';
import { Icon } from 'components/01-atoms/Icon';

import { ChevronIcon } from './Chevron';

type TableOfContentsProps = {
  classes?: string;
  allowOpenClose?: boolean;
  toc: TableOfContentsSectionData[];
};
type TableOfContentsItem = {
  title: string;
  href?: string;
  id?: number;
  items?: TableOfContentsItem[];
  firstItem?: boolean;
  completed?: boolean;
};
type TableOfContentsSectionProps = {
  section: TableOfContentsItem;
  onClick?: VoidFunction;
  isOpen?: boolean;
  sectionCount: number;
};
const TableOfContentsSection = ({
  section,
  onClick,
  isOpen,
  sectionCount
}: TableOfContentsSectionProps) => {
  const firstSection = sectionCount === 1;
  const borderClasses = isOpen ? 'u-transition--border__active' : '';
  const listClasses = isOpen ? 'u-transition--h__active' : '';
  const rotation = isOpen ? 'rotate-180' : 'rotate-0';
  const { isOutsidePlusUser } = useUserType();
  const router = useRouter();
  const { pathname } = router;
  const { elementClicked, options } = useAnalyticsContext();
  const isCourseIntro = pathname === COURSE_INTRODUCTION;
  const handleSectionInteraction = () => {
    // open/close drawer
    if (section.items) {
      onClick();
    }
    // analytics for starting course
    if (firstSection && section.href && isCourseIntro) {
      trackCourseInitiated(section.href);
    }
  };
  const handleSectionKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSectionInteraction();
    }
  };
  const handleFirstItemKeyPress = (e, href: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      trackCourseInitiated(href);
    }
  };
  // eslint-disable-next-line no-underscore-dangle
  const trackCourseInitiated = (href: string) => {
    if (isCourseIntro && firstSection) {
      elementClicked({
        name: 'initiated-course',
        type: 'link',
        destination_url: `${options.baseUrl}${href}`
      });
    }
  };

  const tocSectionHeader = (
    <H2
      id={`toc-header-${sectionCount}`}
      headingText={section.title}
      classes={`toc-section-title mr-6 flex-auto u-text--p-std
            ${
              router.asPath === section.href
                ? 'text-network-storm'
                : 'hover:text-network-storm'
            }
            ${isOutsidePlusUser && section.completed ? 'is-complete' : ''}`}
    />
  );

  return (
    <div>
      {!section.items ? (
        <Link href={section.href} passHref>
          <a
            role="link"
            tabIndex={0}
            onKeyPress={(e) => handleSectionKeyPress(e)}
            onClick={() => handleSectionInteraction()}
          >
            <div className="space-between flex items-center border-b border-network-grey border-opacity-100 px-8 py-4 hover:cursor-pointer">
              {tocSectionHeader}
            </div>
          </a>
        </Link>
      ) : (
        <>
          <button
            id={`toc-control-${sectionCount}`}
            type="button"
            onClick={() => handleSectionInteraction()}
            className="space-between flex w-full items-center px-8 py-4 text-left hover:cursor-pointer"
            aria-expanded={isOpen}
            aria-controls={`toc-section-${sectionCount}`}
          >
            {tocSectionHeader}
            <ChevronIcon
              fillColor="#000"
              classes={`fill-current w-4 flex-none transform transition-rotation ease-in duration-300 ${rotation}`}
              aria-hidden="true"
            />
          </button>
          <section
            id={`toc-section-${sectionCount}`}
            aria-labelledby={`toc-header-${sectionCount}`}
            className={`u-transition--border border-b border-network-grey border-opacity-100 ${borderClasses}`}
          >
            <div
              className={`u-transition--h bg-network-grey-light ${listClasses}`}
            >
              <ul className={`u-text--p-sm px-8 pb-6 pt-4 `}>
                {section.items.map(
                  ({ firstItem, title, completed, href = '/' }, i) => (
                    <li className="mb-1 list-none" key={`${title + i}`}>
                      <Link href={href} passHref>
                        <a
                          className={`flex cursor-pointer items-center ${
                            router.asPath === href
                              ? 'text-network-storm'
                              : 'hover:text-network-storm'
                          }
                        ${isOutsidePlusUser && completed ? 'is-complete' : ''}`}
                          role="link"
                          tabIndex={0}
                          onClick={
                            firstItem ? () => trackCourseInitiated(href) : null
                          }
                          onKeyPress={
                            firstItem
                              ? (e) => handleFirstItemKeyPress(e, href)
                              : null
                          }
                        >
                          {title}
                        </a>
                      </Link>
                    </li>
                  )
                )}
              </ul>
            </div>
          </section>
        </>
      )}
    </div>
  );
};
const TableOfContents = ({
  classes = '',
  allowOpenClose = false,
  toc = []
}: TableOfContentsProps) => {
  const router = useRouter();
  const { courseName, lessonName } = router.query;
  const [isOpen, setOpen] = useState<boolean>(false);
  const [sectionOpen, setSectionOpen] = useState<number | null>(null);
  const handleSectionClick = (index: number) => {
    if (sectionOpen === index) {
      setSectionOpen(null);
    } else {
      setSectionOpen(index);
    }
  };
  useEffect(() => {
    // Loop through sections (and their lessons) to find current section Index
    const lessonSectionIndex = toc.findIndex((section) =>
      section.lessons?.find((item) => item.name === lessonName)
    );
    // Set the open session to that index
    setSectionOpen(lessonSectionIndex);
  }, [lessonName, toc]);

  const toggleOpenClose = () =>
    allowOpenClose && setOpen((prevValue) => !prevValue);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      toggleOpenClose();
    }
  };
  return (
    <div className={`network bg-network-grey-pale ${classes}`}>
      <div
        role="button"
        tabIndex={0}
        onClick={toggleOpenClose}
        onKeyPress={handleKeyPress}
        className={`border-b border-network-grey border-opacity-100 pb-6 pl-7 pt-8 ${
          allowOpenClose ? 'space-between flex items-center pr-6' : ''
        }`}
      >
        <H1 headingText="Table of Contents" classes="h5 uppercase flex-auto" />
        <div className="mr-7 flex h-8 w-8 flex-none items-center justify-center self-start rounded-theme-border-default border   border-network-grey-dark wide:hidden">
          {!isOpen && (
            <ChevronIcon fillColor="#000000" classes="w-4 h-4 cursor-pointer" />
          )}
          {isOpen && (
            <Icon
              strokeColor="#000000"
              type="close"
              size="xsmall"
              classes=" cursor-pointer "
            />
          )}
        </div>
      </div>
      <div
        className={`${isOpen ? 'u-transition--h__active' : 'narrow-x:block'} ${
          allowOpenClose ? 'u-transition--h' : ''
        }`}
      >
        <div key="Introduction">
          <TableOfContentsSection
            section={{
              title: 'Introduction',
              href: routes.courseIntroduction(courseName as string)
            }}
            sectionCount={0}
          />
        </div>
        {toc.map(({ title, lessons }, i) => (
          <div key={`${title + i}`}>
            {!lessons || lessons.length === 1 ? (
              <TableOfContentsSection
                section={{
                  title,
                  href: routes.lessonDetail(
                    courseName as string,
                    lessons[0].name
                  ),
                  completed: lessons[0].completed
                }}
                sectionCount={i + 1}
              />
            ) : (
              <TableOfContentsSection
                section={{
                  title,
                  completed: lessons.some((lesson) => lesson.completed),
                  items: (lessons || []).map(
                    (lesson: Lesson, index: number) => ({
                      title: lesson.title,
                      href: routes.lessonDetail(
                        courseName as string,
                        lesson.name
                      ),
                      firstItem: index === 0,
                      id: lesson.id,
                      completed: lesson.completed
                    })
                  )
                }}
                onClick={() => handleSectionClick(i)}
                isOpen={i === sectionOpen}
                sectionCount={i + 1}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
export default TableOfContents;
